import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { ROUTES } from '../constants/constants';
import { adminAPI, usersAPI, servicesAPI, aiModelsAPI, rolesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useUI } from '../context/UIContext';

// Import admin components
import DashboardTab from '../components/admin/DashboardTab';
import UsersTab from '../components/admin/UsersTab';
import ServicesTab from '../components/admin/ServicesTab';
import RolesTab from '../components/admin/RolesTab';
import ServiceModal from '../components/admin/ServiceModal';
import RoleModal from '../components/admin/RoleModal';
import CompaniesTab from '../components/admin/CompaniesTab';
import CompanyDetailModal from '../components/admin/CompanyDetailModal';
import OrdersTab from '../components/admin/OrdersTab';
import OrderDetailModal from '../components/admin/OrderDetailModal';
import AuditLogsTab from '../components/admin/AuditLogsTab';
import FlagInboxTab from '../components/admin/FlagInboxTab';
import RefreshButton from '../components/common/RefreshButton';
import { useAuth } from '../context/AuthContext';
import SupportQueuePage from './SupportQueuePage';

/* ── Stale-while-revalidate keys (services/viewCache) ──────────────────────
   Returning to the admin dashboard from another page remounts it, so its KPIs
   flashed back to zero and every tab table blanked before reloading. Each
   dataset is cached and rendered instantly on return; paginated tabs fold page
   + filters into the key. ─────────────────────────────────────────────────── */
const DASH_STATS_KEY = 'admin:dash:stats';
const DASH_USERS_KEY = 'admin:dash:users';
const DASH_SERVICES_KEY = 'admin:dash:services';
const DASH_ROLES_KEY = 'admin:dash:roles';
const DASH_PERMISSIONS_KEY = 'admin:dash:permissions';
const dashCompaniesKey = (page, search, type, status) =>
  `admin:dash:companies:${page}|${search}|${type}|${status}`;
const dashDraftsKey = (page, search, type, source, from, to) =>
  `admin:dash:drafts:${page}|${search}|${type}|${source}|${from}|${to}`;

const DEFAULT_DASH_STATS = {
  totalUsers: 0,
  activeUsers: 0,
  adminUsers: 0,
  professionalUsers: 0,
  corporateUsers: 0,
  verifiedUsers: 0,
  totalServices: 0,
  activeServices: 0,
  totalAiModels: 0,
  activeAiModels: 0,
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useUI();
  const { user: currentUser } = useAuth();

  // Get active tab from URL path or search params
  const getActiveTabFromURL = useCallback(() => {
    // Check if the path contains admin section
    const pathSections = location.pathname.split('/');
    const lastSection = pathSections[pathSections.length - 1];
    
    // Map paths to tabs
    const pathToTabMap = {
      'admin': 'dashboard',
      'services': 'services',
      'roles': 'roles',
      'users': 'users',
      'companies': 'companies',
      'orders': 'orders',
      'audit-logs': 'audit-logs',
      'error-logs': 'error-logs',
      'flag-inbox': 'flag-inbox',
      'support-queue': 'support-queue',
    };

    const validTabs = ['dashboard', 'users', 'services', 'roles', 'companies', 'orders', 'audit-logs', 'error-logs', 'flag-inbox', 'support-queue'];
    
    // First try to get tab from path
    if (lastSection && pathToTabMap[lastSection]) {
      const tab = pathToTabMap[lastSection];
      if (validTabs.includes(tab)) {
        return tab;
      }
    }
    
    // Then try to get tab from URL search params
    const tabFromURL = searchParams.get('tab');
    if (tabFromURL && validTabs.includes(tabFromURL)) {
      return tabFromURL;
    }
    
    // Fallback to localStorage if no URL param
    const savedTab = localStorage.getItem('adminDashboardActiveTab');
    if (savedTab && validTabs.includes(savedTab)) {
      return savedTab;
    }
    
    return "dashboard";
  }, [searchParams, location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL);

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromURL = getActiveTabFromURL();
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [searchParams, getActiveTabFromURL, activeTab]);

  // Initialize URL `?tab=…` if missing, BUT preserve any other params already
  // on the URL (mode, targetId, actorId, requestId, fingerprint, q, …).
  //
  // This used to call `setSearchParams({ tab: activeTab })` which silently
  // replaces every search param with just `tab`. That broke deep links like
  // `/admin/audit-logs?mode=audit&targetId=<id>` whenever AdminDashboardPage
  // freshly mounted (e.g. navigating from AdminUsersPage, which is its own
  // page): the init effect would wipe out mode + targetId before AuditLogsTab
  // ever got a chance to read them, so filters silently no-op'd. Use the
  // updater form + merge to keep every other key intact.
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed every dataset from the shared viewCache so a revisit paints instantly
  // and revalidates silently underneath (see keys above).
  const [users, setUsers] = useState(() => getView(DASH_USERS_KEY) ?? []);
  const [services, setServices] = useState(() => getView(DASH_SERVICES_KEY) ?? []);
  const [roles, setRoles] = useState(() => getView(DASH_ROLES_KEY) ?? []);
  const [permissions, setPermissions] = useState(() => getView(DASH_PERMISSIONS_KEY) ?? []);

  const [stats, setStats] = useState(() => ({ ...DEFAULT_DASH_STATS, ...(getView(DASH_STATS_KEY) || {}) }));

  // Mirror the assembled KPI stats to cache so the next mount paints the last
  // known numbers instantly instead of flashing zeros.
  useEffect(() => { setView(DASH_STATS_KEY, stats); }, [stats]);

  // Per-tab loading states
  const [usersLoading, setUsersLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("all");

  // Companies tab state (seeded from cache for the default page-1/no-filter view)
  const [adminCompanies, setAdminCompanies] = useState(() => getView(dashCompaniesKey(1, '', '', ''))?.list ?? []);
  const [companiesPagination, setCompaniesPagination] = useState(() => getView(dashCompaniesKey(1, '', '', ''))?.pagination ?? {});
  const [companySearchInput, setCompanySearchInput] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [companyTypeFilter, setCompanyTypeFilter] = useState("");
  const [companyStatusFilter, setCompanyStatusFilter] = useState("");
  const [companyPage, setCompanyPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Drafts (Orders) tab state (seeded from cache for the default view)
  const [adminDrafts, setAdminDrafts] = useState(() => getView(dashDraftsKey(1, '', '', '', '', ''))?.list ?? []);
  const [draftsPagination, setDraftsPagination] = useState(() => getView(dashDraftsKey(1, '', '', '', '', ''))?.pagination ?? {});
  const [draftSearchInput, setDraftSearchInput] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [draftTypeFilter, setDraftTypeFilter] = useState("");
  const [draftSourceFilter, setDraftSourceFilter] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [draftPage, setDraftPage] = useState(1);
  const [selectedDraft, setSelectedDraft] = useState(null);

  // Debounce company search
  useEffect(() => {
    const timer = setTimeout(() => { setCompanySearch(companySearchInput); setCompanyPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [companySearchInput]);

  // Debounce draft search
  useEffect(() => {
    const timer = setTimeout(() => { setDraftSearch(draftSearchInput); setDraftPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [draftSearchInput]);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [editingService, setEditingService] = useState(null);
  const [editingRole, setEditingRole] = useState(null);


  const [serviceForm, setServiceForm] = useState({
    name: "",
    slug: "",
    description: "",
    price: "",
    category: "document_processing",
    processingType: "automatic",
    estimatedTime: "",
    isActive: true,
    requiredDocuments: [],
    instructions: "",
  });

  const [serviceValidationErrors, setServiceValidationErrors] = useState({});
  const [roleValidationErrors, setRoleValidationErrors] = useState({});

  const [roleForm, setRoleForm] = useState({
    name: "",
    displayName: "",
    description: "",
    permissions: [],
    isActive: true,
  });

  const fetchUsers = useCallback(async () => {
    if (!hasView(DASH_USERS_KEY)) setUsersLoading(true);
    try {
      const response = await usersAPI.getAll();
      if (response.success) {
        const list = response.data.users || [];
        setUsers(list);
        setView(DASH_USERS_KEY, list);
        setStats((prev) => ({
          ...prev,
          totalUsers: list.length,
          activeUsers: list.filter((u) => u.isActive).length,
          adminUsers: list.filter((u) => u.roleName === "admin" || u.roleName === "super_admin").length,
          professionalUsers: list.filter((u) => u.roleName === "professional").length,
          corporateUsers: list.filter((u) => u.roleName === "corporate").length,
          verifiedUsers: list.filter((u) => u.isPhoneVerified).length,
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      if (!hasView(DASH_USERS_KEY)) showToast("Failed to fetch users", "error");
    } finally {
      setUsersLoading(false);
    }
  }, [showToast]);

  const fetchServices = useCallback(async () => {
    if (!hasView(DASH_SERVICES_KEY)) setServicesLoading(true);
    try {
      const response = await servicesAPI.getAllServices();
      if (response.success) {
        const list = response.data.services || [];
        setServices(list);
        setView(DASH_SERVICES_KEY, list);
        setStats((prev) => ({
          ...prev,
          totalServices: list.length,
          activeServices: list.filter((s) => s.isActive).length,
        }));
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      if (!hasView(DASH_SERVICES_KEY)) showToast("Failed to fetch services", "error");
    } finally {
      setServicesLoading(false);
    }
  }, [showToast]);

  // Fetch AI model counts for the dashboard "AI Models" KPI via a lightweight
  // count endpoint — model management itself lives in the AI Control Plane.
  const fetchAiModelStats = useCallback(async () => {
    try {
      const response = await aiModelsAPI.getStats();
      if (response.success) {
        setStats((prev) => ({
          ...prev,
          totalAiModels: response.data.total || 0,
          activeAiModels: response.data.active || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching AI model stats:", error);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    if (!hasView(DASH_ROLES_KEY)) setRolesLoading(true);
    try {
      const response = await rolesAPI.getAll();
      if (response.success) {
        const list = response.data.roles || [];
        setRoles(list);
        setView(DASH_ROLES_KEY, list);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      if (!hasView(DASH_ROLES_KEY)) showToast("Failed to fetch roles", "error");
    } finally {
      setRolesLoading(false);
    }
  }, [showToast]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await rolesAPI.getPermissions();
      if (response.success) {
        const list = response.data.permissions || [];
        setPermissions(list);
        setView(DASH_PERMISSIONS_KEY, list);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      showToast("Failed to fetch permissions", "error");
    }
  }, [showToast]);

  const fetchAdminCompanies = useCallback(async (p = companyPage) => {
    const key = dashCompaniesKey(p, companySearch, companyTypeFilter, companyStatusFilter);
    if (!hasView(key)) setCompaniesLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (companySearch) params.search = companySearch;
      if (companyTypeFilter) params.type = companyTypeFilter;
      if (companyStatusFilter) params.status = companyStatusFilter;
      const response = await adminAPI.getAllCompanies(params);
      if (response.success) {
        const list = response.data.companies || [];
        const pg = response.data.pagination || {};
        setAdminCompanies(list);
        setCompaniesPagination(pg);
        setView(key, { list, pagination: pg });
      }
    } catch (error) {
      console.error("Error fetching admin companies:", error);
    } finally {
      setCompaniesLoading(false);
    }
  }, [companySearch, companyTypeFilter, companyStatusFilter, companyPage]);

  const fetchAdminDrafts = useCallback(async (p = draftPage) => {
    const key = dashDraftsKey(p, draftSearch, draftTypeFilter, draftSourceFilter, draftDateFrom, draftDateTo);
    if (!hasView(key)) setOrdersLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (draftSearch) params.search = draftSearch;
      if (draftTypeFilter) params.documentType = draftTypeFilter;
      if (draftSourceFilter) params.docSource = draftSourceFilter;
      if (draftDateFrom) params.dateFrom = draftDateFrom;
      if (draftDateTo) params.dateTo = draftDateTo;
      const response = await adminAPI.getAllDrafts(params);
      if (response.success) {
        const list = response.data.drafts || [];
        const pg = response.data.pagination || {};
        setAdminDrafts(list);
        setDraftsPagination(pg);
        setView(key, { list, pagination: pg });
      }
    } catch (error) {
      console.error("Error fetching admin drafts:", error);
    } finally {
      setOrdersLoading(false);
    }
  }, [draftSearch, draftTypeFilter, draftSourceFilter, draftDateFrom, draftDateTo, draftPage]);

  // Fetch data on component mount
  useEffect(() => {
    fetchUsers();
    fetchServices();
    fetchAiModelStats(); // populates the dashboard "AI Models" KPI count
    // Also fetch data for the initial active tab
    if (activeTab === "roles") {
      fetchRoles();
      fetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === "roles") {
      fetchRoles();
      fetchPermissions();
    } else if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "services") {
      fetchServices();
    } else if (activeTab === "companies") {
      fetchAdminCompanies();
    } else if (activeTab === "orders") {
      fetchAdminDrafts();
    }
  }, [activeTab, fetchRoles, fetchUsers, fetchServices, fetchPermissions, fetchAdminCompanies, fetchAdminDrafts]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = searchTerm === "" || 
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm);
      
      const matchesRole = roleFilter === "all" || user.roleName === roleFilter;
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "active" && user.isActive) ||
        (statusFilter === "inactive" && !user.isActive);
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Filter services based on search and category
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesSearch = serviceSearchTerm === "" || 
        service.name?.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(serviceSearchTerm.toLowerCase());
      
      const matchesCategory = serviceCategoryFilter === "all" || service.category === serviceCategoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  }, [services, serviceSearchTerm, serviceCategoryFilter]);

  // Per-tab page title + helpful subhead. Used by the top header (replacing
  // the previous static "Admin Dashboard / System administration and
  // management") and by AppLayout's browser tab title. Subheads describe
  // what the page is for in one short line — useful as a reminder for
  // admins who just landed via a deep link.
  const HEADER_BY_TAB = {
    'dashboard':   { title: 'Admin Dashboard',     subhead: "Platform-wide activity, revenue, and health at a glance" },
    'users':       { title: 'User Management',     subhead: 'Manage accounts, roles, lockouts, and per-user audit history' },
    'companies':   { title: 'Companies',           subhead: 'Every company profile on the platform — drafts, professionals, and history' },
    'orders':      { title: 'Orders',              subhead: 'Order pipeline, payment status, document drafts, and lifecycle history' },
    'services':    { title: 'Services Catalog',    subhead: 'Catalog of products, resolutions, and add-ons users can purchase' },
    'roles':       { title: 'Roles & Permissions', subhead: 'Define what each role can read, write, and approve' },
    'audit-logs':  { title: 'Audit Logs',          subhead: 'Tamper-evident record of every admin and user action' },
    'error-logs':  { title: 'Error Logs',          subhead: 'Grouped runtime errors across backend and frontend, by fingerprint' },
    'flag-inbox':  { title: 'Flag Inbox',          subhead: 'Triage flags filed by admins against peer users for super-admin review' },
    'support-queue': { title: 'Support Queue',     subhead: 'All support tickets, replies, and team assignments in one place' },
  };
  const header = HEADER_BY_TAB[activeTab] || HEADER_BY_TAB.dashboard;

  // Tabs that own their own action set (Audit & Error Logs has Verify
  // Integrity + Export CSV + its own Refresh) render those buttons into
  // this slot via a portal — see `headerActionsEl` below. Lets us keep the
  // state colocated with AuditLogsTab while still surfacing the actions
  // next to the page title where the user wants them.
  const [headerActionsEl, setHeaderActionsEl] = useState(null);

  // Per-tab refresh affordance. Lifted out of the inner tab components so
  // every admin page has its Refresh button in the same spot — right of the
  // page title — exactly like AdminUsersPage's `/admin/users` view. Avoids
  // the previous problem where a full-tab loading splash would unmount the
  // inner RefreshButton on click. `dashboard` has no single fetch (it
  // composes many widgets) and audit-logs / flag-inbox manage their own
  // refresh state internally; for those three we don't render the top
  // button (their own affordances stay where they are).
  const REFRESH_BY_TAB = {
    users:       { onClick: fetchUsers,                          loading: usersLoading },
    companies:   { onClick: () => fetchAdminCompanies(companyPage), loading: companiesLoading },
    orders:      { onClick: () => fetchAdminDrafts(draftPage),     loading: ordersLoading },
    services:    { onClick: fetchServices,                       loading: servicesLoading },
    roles:       { onClick: fetchRoles,                          loading: rolesLoading },
  };
  const headerRefresh = REFRESH_BY_TAB[activeTab];

  return (
    <AppLayout pageTitle={header.title}>
      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Header — title + subhead reflect the active tab so an admin landing
            on a deep link (e.g. /admin/audit-logs) sees the page they're on,
            not the generic "Admin Dashboard" wordmark.
            Refresh sits flush right beside the title for every tab that has
            a parent-owned fetch, matching the placement on AdminUsersPage. */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-[28px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{header.title}</h1>
            <p className="text-[13px] text-[#7a7060]">{header.subhead}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Portal target for tab-specific actions (Verify Integrity /
                Export CSV / Refresh on the Audit & Error Logs tab). Sits
                BEFORE the generic Refresh so its Refresh wins when both
                would render — but in practice REFRESH_BY_TAB skips
                audit-logs so they never both render. */}
            <div ref={setHeaderActionsEl} className="flex items-center gap-2 flex-wrap" />
            {headerRefresh && (
              <RefreshButton onClick={headerRefresh.onClick} loading={headerRefresh.loading} />
            )}
          </div>
        </div>

        {/* Tab navigation removed — the admin area is sidebar-driven now.
            activeTab is still derived from the URL (sidebar nav → routes →
            pathToTabMap), and the header above reflects the active section. */}

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {activeTab === 'dashboard' && (
            <DashboardTab
              stats={stats}
              users={users.slice(0, 8)}
              loading={usersLoading}
              navigate={navigate}
            />
          )}

          {activeTab === 'users' && (
            <UsersTab
              users={filteredUsers}
              loading={usersLoading}
              searchTerm={searchTerm}
              roleFilter={roleFilter}
              statusFilter={statusFilter}
              onSearchChange={setSearchTerm}
              onRoleFilterChange={setRoleFilter}
              onStatusFilterChange={setStatusFilter}
              onRefresh={fetchUsers}
            />
          )}
          
          {activeTab === 'services' && (
            <ServicesTab
              services={filteredServices}
              loading={servicesLoading}
              searchTerm={serviceSearchTerm}
              categoryFilter={serviceCategoryFilter}
              onSearchChange={setServiceSearchTerm}
              onCategoryFilterChange={setServiceCategoryFilter}
              onRefresh={fetchServices}
              onAddService={() => {
                setEditingService(null);
                setServiceForm({
                  name: "",
                  slug: "",
                  description: "",
                  price: "",
                  category: "document_processing",
                  processingType: "automatic",
                  estimatedTime: "",
                  isActive: true,
                  requiredDocuments: [],
                  instructions: "",
                });
                setShowServiceModal(true);
              }}
              onEditService={(service) => {
                setEditingService(service);
                const formData = {
                  name: service.name,
                  slug: service.slug,
                  description: service.description,
                  price: service.price?.toString() || "",
                  category: service.category,
                  processingType: service.processingType,
                  estimatedTime: service.estimatedTime,
                  isActive: service.isActive,
                  requiredDocuments: service.requiredDocuments || [],
                  instructions: service.instructions || "",
                };
                setServiceForm(formData);
                setShowServiceModal(true);
              }}
              onDeleteService={async (serviceId) => {
                if (!window.confirm("Delete this service?")) return;
                try {
                  await servicesAPI.deleteService(serviceId);
                  showToast("Service deleted", "success");
                  fetchServices();
                } catch (error) {
                  showToast("Failed to delete service", "error");
                }
              }}
              onToggleService={async (serviceId) => {
                try {
                  const service = services.find((s) => s._id === serviceId);
                  await servicesAPI.updateService(serviceId, { ...service, isActive: !service.isActive });
                  setServices((prev) => prev.map((s) => s._id === serviceId ? { ...s, isActive: !s.isActive } : s));
                  showToast("Service status updated", "success");
                } catch (error) {
                  showToast("Failed to update service", "error");
                }
              }}
            />
          )}
          
          {activeTab === 'roles' && (
            <RolesTab
              roles={roles}
              permissions={permissions}
              loading={rolesLoading}
              onRefresh={fetchRoles}
              onAddRole={() => {
                setEditingRole(null);
                setRoleForm({
                  name: "",
                  displayName: "",
                  description: "",
                  permissions: [],
                  isActive: true,
                });
                setShowRoleModal(true);
              }}
              onEditRole={(role) => {
                setEditingRole(role);
                const formData = {
                  name: role.name,
                  displayName: role.displayName || role.name,
                  description: role.description || "",
                  permissions: role.permissions?.map((p) => p._id || p) || [],
                  isActive: role.isActive !== false,
                };
                setRoleForm(formData);
                setShowRoleModal(true);
              }}
              onDeleteRole={async (roleId) => {
                if (!window.confirm("Delete this role?")) return;
                try {
                  await rolesAPI.delete(roleId);
                  showToast("Role deleted", "success");
                  fetchRoles();
                } catch (error) {
                  showToast(error.message || "Failed to delete role", "error");
                }
              }}
            />
          )}

          {activeTab === 'companies' && (
            <CompaniesTab
              companies={adminCompanies}
              loading={companiesLoading}
              pagination={companiesPagination}
              search={companySearchInput}
              typeFilter={companyTypeFilter}
              statusFilter={companyStatusFilter}
              onSearchChange={setCompanySearchInput}
              onTypeFilterChange={(v) => { setCompanyTypeFilter(v); setCompanyPage(1); }}
              onStatusFilterChange={(v) => { setCompanyStatusFilter(v); setCompanyPage(1); }}
              onRefresh={() => fetchAdminCompanies(companyPage)}
              onPageChange={(p) => { setCompanyPage(p); fetchAdminCompanies(p); }}
              onViewCompany={async (company) => {
                try {
                  const res = await adminAPI.getCompanyById(company._id);
                  if (res.success) setSelectedCompany(res.data.company);
                  else setSelectedCompany(company);
                } catch (_) {
                  setSelectedCompany(company);
                }
              }}
            />
          )}

          {activeTab === 'orders' && (
            <OrdersTab
              orders={adminDrafts}
              loading={ordersLoading}
              pagination={draftsPagination}
              search={draftSearchInput}
              typeFilter={draftTypeFilter}
              sourceFilter={draftSourceFilter}
              dateFrom={draftDateFrom}
              dateTo={draftDateTo}
              onSearchChange={setDraftSearchInput}
              onTypeFilterChange={(v) => { setDraftTypeFilter(v); setDraftPage(1); }}
              onSourceFilterChange={(v) => { setDraftSourceFilter(v); setDraftPage(1); }}
              onDateFromChange={(v) => { setDraftDateFrom(v); setDraftPage(1); }}
              onDateToChange={(v) => { setDraftDateTo(v); setDraftPage(1); }}
              onRefresh={() => fetchAdminDrafts(draftPage)}
              onPageChange={(p) => { setDraftPage(p); fetchAdminDrafts(p); }}
              onViewOrder={async (draft) => {
                try {
                  const res = await adminAPI.getDraftById(draft._id);
                  if (res.success) setSelectedDraft(res.data.draft);
                  else setSelectedDraft(draft);
                } catch (_) {
                  setSelectedDraft(draft);
                }
              }}
            />
          )}

          {activeTab === 'support-queue' && (
            <SupportQueuePage />
          )}

          {activeTab === 'audit-logs' && (
            <AuditLogsTab defaultMode="audit" headerActionsEl={headerActionsEl} />
          )}

          {activeTab === 'error-logs' && (
            <AuditLogsTab defaultMode="errors" headerActionsEl={headerActionsEl} />
          )}

          {activeTab === 'flag-inbox' && (
            <FlagInboxTab currentUserRole={currentUser?.roleName} />
          )}
        </div>
      </div>

      {/* Company Detail Modal */}
      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}

      {/* Draft Detail Modal */}
      {selectedDraft && (
        <OrderDetailModal
          order={selectedDraft}
          onClose={() => setSelectedDraft(null)}
        />
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <ServiceModal
          service={editingService}
          form={serviceForm}
          validationErrors={serviceValidationErrors}
          onClose={() => {
            setShowServiceModal(false);
            setEditingService(null);
            setServiceValidationErrors({});
          }}
          onSave={async (formData) => {
            try {
              if (editingService) {
                await servicesAPI.updateService(editingService._id, formData);
                showToast("Service updated successfully", "success");
              } else {
                await servicesAPI.createService(formData);
                showToast("Service created successfully", "success");
              }
              setShowServiceModal(false);
              fetchServices();
            } catch (error) {
              console.error("Error saving service:", error);
              showToast("Failed to save service", "error");
            }
          }}
          onChange={setServiceForm}

        />
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <RoleModal
          role={editingRole}
          form={roleForm}
          validationErrors={roleValidationErrors}
          permissions={permissions}
          onClose={() => {
            setShowRoleModal(false);
            setEditingRole(null);
            setRoleValidationErrors({});
          }}
          onSave={async (formData) => {
            try {
              if (editingRole) {
                await rolesAPI.update(editingRole._id, formData);
                showToast("Role updated successfully", "success");
              } else {
                await rolesAPI.create(formData);
                showToast("Role created successfully", "success");
              }
              setShowRoleModal(false);
              fetchRoles();
            } catch (error) {
              console.error("Error saving role:", error);
              showToast("Failed to save role", "error");
            }
          }}
          onChange={setRoleForm}
        />
      )}
    </AppLayout>
  );
}
